// What a control's action becomes on the API (device-controls WEB-CMD-01, -04, -05, WEB-SET-03): a profile command
// with its params, or a one-setting change. Pure, so the rules are tested without a page.
import type { Widget, WidgetInput } from "../../../widgets/types";
import type { PageDocument } from "../page/profile";

export interface ParamSpec {
  name: string;
  min?: number;
  max?: number;
  unit?: string;
}

export type Resolved =
  | {
      kind: "command";
      command: string;
      params: Record<string, unknown>;
      /** Params the person must fill in before it is sent. */
      ask: ParamSpec[];
      guarded: boolean;
      /** The confirm text in the app's language: ActionInfo.confirm, else the page document's. */
      confirmText?: string;
      /** The command's name to ask with when there is no confirm text. */
      label?: string;
    }
  | { kind: "setting"; values: Record<string, unknown> };

type Entry = Record<string, unknown>;
const isObject = (v: unknown): v is Entry => v !== null && typeof v === "object" && !Array.isArray(v);

function commandEntry(doc: PageDocument | undefined, key: string): Entry | undefined {
  return (doc?.commands ?? []).find((c) => c.key === key);
}

/** The command whose profile `value` matches the asked one (start is 1, stop is 0), else the first. */
export function pickCommand(doc: PageDocument | undefined, options: string[], value: unknown): string | undefined {
  const asNumber = typeof value === "boolean" ? (value ? 1 : 0) : value;
  return (
    options.find((k) => {
      const v = commandEntry(doc, k)?.value;
      return v === value || v === asNumber || (typeof v === "boolean" && (v ? 1 : 0) === asNumber);
    }) ?? options[0]
  );
}

/**
 * A command: its params come from the action's value when the command has exactly one param (a slider's level),
 * and every other param is asked. A setting: the value under its key.
 */
export function resolveAction(
  doc: PageDocument | undefined,
  action: { kind: "command" | "setting"; key: string; value: unknown; options?: string[]; confirm?: boolean },
): Resolved {
  if (action.kind === "setting") return { kind: "setting", values: { [action.key]: action.value } };
  const command = pickCommand(doc, action.options ?? [action.key], action.value) ?? action.key;
  const entry = commandEntry(doc, command);
  const specs: ParamSpec[] = isObject(entry?.params)
    ? Object.entries(entry.params).map(([name, p]) => ({
        name,
        ...(isObject(p) && typeof p.min === "number" ? { min: p.min } : {}),
        ...(isObject(p) && typeof p.max === "number" ? { max: p.max } : {}),
        ...(isObject(p) && typeof p.unit === "string" ? { unit: p.unit } : {}),
      }))
    : [];
  const fromValue = specs.length === 1 && typeof action.value === "number";
  const params: Record<string, unknown> = fromValue && specs[0] ? { [specs[0].name]: action.value } : {};
  const confirmText =
    typeof entry?.confirm === "string" && !entry.confirm.startsWith("i18n:") ? entry.confirm : undefined;
  return {
    kind: "command",
    command,
    params,
    ask: fromValue ? [] : specs,
    guarded: entry?.guarded === true || action.confirm === true,
    ...(confirmText ? { confirmText } : {}),
  };
}

/** A param value inside its limits, or why not. */
export function paramProblem(spec: ParamSpec, raw: string): "required" | "range" | undefined {
  if (raw.trim() === "") return "required";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "required";
  if ((spec.min !== undefined && n < spec.min) || (spec.max !== undefined && n > spec.max)) return "range";
  return undefined;
}

export type CommandState = "queued" | "delivered" | "executed" | "failed" | "expired" | "superseded" | "cancelled";

export const OPEN: ReadonlySet<string> = new Set(["queued", "delivered"]);

/** The widget-fixture ActionState state for a command's state: superseded and cancelled leave the control idle. */
export function widgetState(s: CommandState): "queued" | "delivered" | "executed" | "failed" | "expired" | "idle" {
  return s === "superseded" || s === "cancelled" ? "idle" : s;
}

/**
 * The widget's reported value already is the asked one, reported at or after the request (WEB-CMD-02): the value
 * ends the wait even while the command still reads queued or delivered.
 */
export function reportedAsAsked(widget: Widget, input: WidgetInput, asked: unknown, requestedAt: string): boolean {
  const report = (widget.bindings ?? []).find((b) => b.datapoint);
  const latest = report?.datapoint ? input.points?.[`${report.device}/${report.datapoint}`]?.latest : undefined;
  if (!latest || latest.value !== asked) return false;
  return new Date(latest.at).getTime() >= new Date(requestedAt).getTime();
}

/** How long to wait before reading an open command again: 2 s, growing by half each read, at most 10 s. */
export function pollDelay(reads: number): number {
  return Math.min(2_000 * 1.5 ** Math.max(0, reads - 1), 10_000);
}

/** A sleeping device's expected delivery, worth saying when it is more than a minute away (WEB-CMD-03). */
export function laterDelivery(expected: string | null | undefined, now: Date): Date | undefined {
  if (!expected) return undefined;
  const at = new Date(expected);
  return at.getTime() - now.getTime() > 60_000 ? at : undefined;
}
